import { type CSSProperties } from 'react';

/**
 * Typography contract for presentation text blocks, shared by the public viewer's static renderer
 * (`PublicShowcaseTextBlock`) and the edit canvas's live TipTap editor
 * (`ShowcaseEditorCanvasTextBlockEditor`) so what authors see while typing is exactly what
 * the public page renders.
 *
 * Tailwind's JIT needs literal class strings, so the same node styling necessarily exists in two
 * spellings: the viewer constants below apply classes straight onto the elements the static
 * renderer emits, while `PRESENTATION_TEXT_BLOCK_EDITOR_CONTENT_CLASS` re-expresses them as
 * descendant selectors over the live ProseMirror DOM (which we don't control element-by-element).
 * The two spellings live in THIS one module so they are maintained together — change them in
 * lockstep.
 */

/** Both surfaces' outer wrapper: the collection column's 16px gutter, the proto's 15px body size. */
// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_CONTAINER_CLASS = 'w-full p-4 text-[15px] ads:text-primary text-gray-12';

/** The proto's inset treatment: a centered narrow reading column. */
// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_INSET_CLASS = 'mx-auto max-w-[42rem]';

/**
 * The proto's 10px rhythm between top-level doc nodes — the viewer's wrapper children ARE the doc
 * nodes; the editor's equivalent selector (via `.ProseMirror`) lives in the editor content class.
 */
export const PRESENTATION_TEXT_BLOCK_VIEWER_RHYTHM_CLASS = '[&>*+*]:mt-2.5';

/**
 * The --headline-font-* vars exist only on the public collection and board page roots (their
 * headline-font design setting); when the link has none they are unset and the fallbacks keep the
 * body font and each level's own weight (same contract as the gallery board titles). The style var
 * lives here with the family so a picked italic variant actually renders italic.
 */
// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_HEADING_FONT_CLASS =
  'font-[family-name:var(--headline-font-family,inherit)] [font-style:var(--headline-font-style,normal)]';

// em-based sizes scale off the block's 15px body size (proto parity); none map to configured
// text-N steps. The weight is read from --headline-font-weight rather than hardcoded so headings
// apply the one face the headline-font loader requested — a hardcoded 700 against a regular-only
// load renders as browser-synthesized fake bold. Each level's fallback is its own pre-var weight.
export const PRESENTATION_TEXT_BLOCK_HEADING_CLASS_BY_LEVEL: Record<number, string> = {
  1: 'text-[2em] leading-[1.2] [font-weight:var(--headline-font-weight,700)]',
  2: 'text-[1.5em] leading-[1.25] [font-weight:var(--headline-font-weight,600)]',
  3: 'text-[1.25em] leading-[1.3] [font-weight:var(--headline-font-weight,600)]',
};

// 'left' is the TextAlign default and needs no class.
export const PRESENTATION_TEXT_BLOCK_TEXT_ALIGN_CLASS: Record<string, string> = {
  center: 'text-center',
  justify: 'text-justify',
  right: 'text-right',
};

export const PRESENTATION_TEXT_BLOCK_PARAGRAPH_CLASS = 'leading-[1.6]';

// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_BULLET_LIST_CLASS = 'list-disc pl-[1.5em]';

// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_ORDERED_LIST_CLASS = 'list-decimal pl-[1.5em]';

// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_LIST_ITEM_CLASS = 'mt-1';

// biome-ignore lint/plugin: viewer and editor spellings stay in this module — see the file header
export const PRESENTATION_TEXT_BLOCK_LINK_CLASS = 'ads:text-accent text-blue-10 underline';

/**
 * React's CSSProperties has no index signature for custom properties, so the headline-color var
 * the editor content class reads must be named explicitly for a style object to typecheck without
 * an assertion (precedent: PublicShowcasePage's headline font var style). The viewer needs no
 * var — its static heading components take the color as an inline style directly.
 */
export type PresentationTextBlockHeadlineColorVarStyle = CSSProperties & {
  '--presentation-text-block-headline-color': string;
};

/**
 * The editor spelling of everything above, as descendant selectors on the live ProseMirror DOM,
 * plus editor-only chrome that has no viewer counterpart: the focus outline reset and the
 * Placeholder extension's empty-doc hint (TipTap only sets `data-placeholder`; rendering it is the
 * integrator's CSS). Alignment needs no selectors here — the TextAlign extension writes inline
 * `text-align` styles onto the live nodes.
 */
export const PRESENTATION_TEXT_BLOCK_EDITOR_CONTENT_CLASS = [
  '[&_.ProseMirror]:outline-none',
  // PRESENTATION_TEXT_BLOCK_VIEWER_RHYTHM_CLASS
  '[&_.ProseMirror>*+*]:mt-2.5',
  // PRESENTATION_TEXT_BLOCK_HEADING_FONT_CLASS + headline color var + HEADING_CLASS_BY_LEVEL
  '[&_.ProseMirror_h1]:font-[family-name:var(--headline-font-family,inherit)] [&_.ProseMirror_h1]:[color:var(--presentation-text-block-headline-color,inherit)] [&_.ProseMirror_h1]:[font-style:var(--headline-font-style,normal)] [&_.ProseMirror_h1]:text-[2em] [&_.ProseMirror_h1]:[font-weight:var(--headline-font-weight,700)] [&_.ProseMirror_h1]:leading-[1.2]',
  '[&_.ProseMirror_h2]:font-[family-name:var(--headline-font-family,inherit)] [&_.ProseMirror_h2]:[color:var(--presentation-text-block-headline-color,inherit)] [&_.ProseMirror_h2]:[font-style:var(--headline-font-style,normal)] [&_.ProseMirror_h2]:text-[1.5em] [&_.ProseMirror_h2]:[font-weight:var(--headline-font-weight,600)] [&_.ProseMirror_h2]:leading-[1.25]',
  '[&_.ProseMirror_h3]:font-[family-name:var(--headline-font-family,inherit)] [&_.ProseMirror_h3]:[color:var(--presentation-text-block-headline-color,inherit)] [&_.ProseMirror_h3]:[font-style:var(--headline-font-style,normal)] [&_.ProseMirror_h3]:text-[1.25em] [&_.ProseMirror_h3]:[font-weight:var(--headline-font-weight,600)] [&_.ProseMirror_h3]:leading-[1.3]',
  // PRESENTATION_TEXT_BLOCK_PARAGRAPH_CLASS
  '[&_.ProseMirror_p]:leading-[1.6]',
  // PRESENTATION_TEXT_BLOCK_BULLET_LIST_CLASS / ORDERED_LIST_CLASS / LIST_ITEM_CLASS
  '[&_.ProseMirror_ul]:list-disc [&_.ProseMirror_ul]:pl-[1.5em]',
  '[&_.ProseMirror_ol]:list-decimal [&_.ProseMirror_ol]:pl-[1.5em]',
  '[&_.ProseMirror_li]:mt-1',
  // PRESENTATION_TEXT_BLOCK_LINK_CLASS
  '[&_.ProseMirror_a]:ads:text-accent [&_.ProseMirror_a]:text-blue-10 [&_.ProseMirror_a]:underline',
  // Placeholder rendering (editor-only)
  '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
  '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
  '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
  '[&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0',
  '[&_.ProseMirror_p.is-editor-empty:first-child::before]:ads:text-placeholder [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-8',
].join(' ');
